import { useEffect, useRef } from 'react';

interface WaveOverlayProps {
  colors?: {
    primary?: string;
    secondary?: string;
  };
  opacity?: number;
}

export default function WaveOverlay({ colors, opacity = 0.2 }: WaveOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    const drawWave = (yOffset: number, amplitude: number, frequency: number, speed: number, color: string, alpha: number) => {
      ctx.beginPath();
      ctx.moveTo(0, canvas.height / 2 + yOffset);

      for (let x = 0; x < canvas.width; x++) {
        const y = Math.sin((x * frequency) + (time * speed)) * amplitude + canvas.height / 2 + yOffset;
        ctx.lineTo(x, y);
      }

      ctx.lineTo(canvas.width, canvas.height);
      ctx.lineTo(0, canvas.height);
      ctx.closePath();

      const [r,g,b] = parseCssColorToRgb(color);
      ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
      ctx.fill();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Read header accent each frame to respond to theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      const color1 = colors?.primary || accent;
      const color2 = colors?.secondary || accent;

      drawWave(-100, 50, 0.01, 0.02, color1, 0.1);
      drawWave(-50, 40, 0.015, 0.025, color2, 0.08);
      drawWave(0, 60, 0.008, 0.015, color1, 0.12);

      time += 0.5;
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [colors, opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
